import { z } from "zod";
/** Controlled image roles for Flat 2.0 / certified surface rendering. */
export const MediaSlotIdSchema = z.enum([
    "heroBackgroundImage",
    "heroSideImage",
    "serviceCardImage",
    "galleryImage",
    "beforeAfterImage",
    "proofImage",
    "teamImage",
    "locationImage",
    "ctaBackgroundImage",
    "processImage",
    "fallbackBrandTexture",
]);
export const MediaAssetSourceSchema = z.enum(["client", "agency", "proof-fixture"]);
export const MediaSlotDefinitionSchema = z
    .object({
    slotId: MediaSlotIdSchema,
    allowedSections: z.array(z.string().min(1)),
    aspectRatio: z.string().min(1),
    desktopCrop: z.string().min(1),
    mobileCrop: z.string().min(1),
    overlayRules: z.string().min(1),
    fallbackBehavior: z.string().min(1),
    qgCheckIds: z.array(z.string().min(1)).default([]),
    clientReplaceable: z.boolean().default(false),
    agencyReplaceable: z.boolean().default(false),
})
    .strict();
export const MediaAssignmentSchema = z
    .object({
    mediaSlot: MediaSlotIdSchema,
    assetId: z.string().min(1),
    cropFocus: z.string().min(1).optional(),
})
    .strict();
export const BusinessGraphAssetV1Schema = z
    .object({
    id: z.string().min(1),
    url: z.string().optional(),
    mediaSlot: MediaSlotIdSchema.optional(),
    altText: z.string().min(1).optional(),
    source: MediaAssetSourceSchema.optional(),
    fixtureKey: z.string().min(1).optional(),
})
    .strict();
export const MediaResolutionSourceSchema = z.enum(["asset", "fallback", "proof-fixture", "omit"]);
export const ResolvedMediaSlotV1Schema = z
    .object({
    slotId: MediaSlotIdSchema,
    source: MediaResolutionSourceSchema,
    url: z.string().optional(),
    altText: z.string().min(1),
    demoFixture: z.boolean().default(false),
    evidenceRef: z.string().optional(),
    aspectRatio: z.string().min(1),
    cssCropClass: z.string().min(1),
})
    .strict();
export const FLAT2_MEDIA_SLOT_DEFINITIONS = [
    {
        slotId: "heroBackgroundImage",
        allowedSections: ["hero"],
        aspectRatio: "16 / 9",
        desktopCrop: "cover center top",
        mobileCrop: "cover center",
        overlayRules: "required-scrim",
        fallbackBehavior: "fallbackBrandTexture",
        qgCheckIds: ["surface-quality.media-hero-scrim"],
        clientReplaceable: false,
        agencyReplaceable: true,
    },
    {
        slotId: "heroSideImage",
        allowedSections: ["hero"],
        aspectRatio: "4 / 5",
        desktopCrop: "cover center",
        mobileCrop: "cover center",
        overlayRules: "none",
        fallbackBehavior: "abstract-panel",
        qgCheckIds: ["surface-quality.media-alt-present"],
        clientReplaceable: true,
        agencyReplaceable: true,
    },
    {
        slotId: "serviceCardImage",
        allowedSections: ["services"],
        aspectRatio: "3 / 2",
        desktopCrop: "cover center",
        mobileCrop: "cover center",
        overlayRules: "none",
        fallbackBehavior: "texture-tile",
        qgCheckIds: ["surface-quality.media-alt-present"],
        clientReplaceable: true,
        agencyReplaceable: true,
    },
    {
        slotId: "galleryImage",
        allowedSections: ["gallery"],
        aspectRatio: "4 / 3",
        desktopCrop: "cover center",
        mobileCrop: "cover center",
        overlayRules: "none",
        fallbackBehavior: "omit-section",
        qgCheckIds: [],
        clientReplaceable: true,
        agencyReplaceable: true,
    },
    {
        slotId: "beforeAfterImage",
        allowedSections: ["trust", "gallery"],
        aspectRatio: "2 / 1",
        desktopCrop: "cover center",
        mobileCrop: "cover center",
        overlayRules: "illustration-label-required",
        fallbackBehavior: "omit",
        qgCheckIds: ["surface-quality.media-before-after-evidence"],
        clientReplaceable: true,
        agencyReplaceable: true,
    },
    {
        slotId: "proofImage",
        allowedSections: ["trust"],
        aspectRatio: "3 / 2",
        desktopCrop: "cover center",
        mobileCrop: "cover center",
        overlayRules: "none",
        fallbackBehavior: "text-only-proof",
        qgCheckIds: ["surface-quality.media-proof-evidence"],
        clientReplaceable: true,
        agencyReplaceable: true,
    },
    {
        slotId: "teamImage",
        allowedSections: ["trust"],
        aspectRatio: "1 / 1",
        desktopCrop: "cover center",
        mobileCrop: "cover center",
        overlayRules: "none",
        fallbackBehavior: "omit",
        qgCheckIds: [],
        clientReplaceable: true,
        agencyReplaceable: true,
    },
    {
        slotId: "locationImage",
        allowedSections: ["areas"],
        aspectRatio: "16 / 9",
        desktopCrop: "cover center top",
        mobileCrop: "cover center",
        overlayRules: "optional-caption",
        fallbackBehavior: "map-texture",
        qgCheckIds: [],
        clientReplaceable: true,
        agencyReplaceable: true,
    },
    {
        slotId: "ctaBackgroundImage",
        allowedSections: ["cta-band"],
        aspectRatio: "21 / 9",
        desktopCrop: "cover center",
        mobileCrop: "cover center",
        overlayRules: "required-dark-scrim",
        fallbackBehavior: "solid-ink-band",
        qgCheckIds: ["surface-quality.media-cta-scrim"],
        clientReplaceable: false,
        agencyReplaceable: true,
    },
    {
        slotId: "processImage",
        allowedSections: ["process"],
        aspectRatio: "3 / 2",
        desktopCrop: "cover center",
        mobileCrop: "cover center",
        overlayRules: "none",
        fallbackBehavior: "omit",
        qgCheckIds: [],
        clientReplaceable: true,
        agencyReplaceable: true,
    },
    {
        slotId: "fallbackBrandTexture",
        allowedSections: ["hero", "cta-band", "services"],
        aspectRatio: "1 / 1",
        desktopCrop: "tile",
        mobileCrop: "tile",
        overlayRules: "n/a",
        fallbackBehavior: "css-pattern",
        qgCheckIds: [],
        clientReplaceable: false,
        agencyReplaceable: false,
    },
];
//# sourceMappingURL=media-slot.js.map